import type {ReactElement} from 'react';
import React from 'react';
import {ButtonItemSecondary} from '@Components/contextual-menu/components/button-item-secondary';
import {ButtonSvgItem} from '@Components/contextual-menu/components/button-svg-item';
import type {IconPlacement, Size, Type} from '@Components/button';
import {Button} from '@Components/button';
import {AiButton} from '@Components/ai-button';
import type {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import styles from './button-items.module.scss';

interface ButtonItemsProps {
  data: ButtonItemsStorageType;
  isInlineOption?: boolean;
  containerClassName?: string;
}

export type ButtonItemsStorageType = Array<ButtonItemWithSVG> | Array<ButtonItemSecondaryStorage> | Array<CustomButtonItemStorage> | Array<AiButtonItemStorage>;

export enum ContextualButtonItemType {
  SECONDARY_BUTTON = 'SECONDARY_BUTTON',
  SVG_BUTTON = 'SVG_BUTTON',
  CUSTOM_BUTTON = 'CUSTOM_BUTTON',
  AI_BUTTON = 'AI_BUTTON',
}

export interface ButtonItemWithSVG extends BaseButtonItem {
  type: ContextualButtonItemType.SVG_BUTTON;
  svgPath: string;
  text: string;
  isSelected?: boolean;
}

export interface ButtonItemSecondaryStorage extends BaseButtonItem {
  type: ContextualButtonItemType.SECONDARY_BUTTON;
  buttonText: string;
  buttonIcon?: string;
  text?: string;
}

export interface CustomButtonItemStorage extends BaseButtonItem {
  type: ContextualButtonItemType.CUSTOM_BUTTON;
  buttonType: Type;
  buttonSize: Size;
  text: string;
  icon?: string;
  iconPlacement?: IconPlacement;
}

export interface AiButtonItemStorage extends BaseButtonItem {
  variant: AIButtonVariant;
  isLoading: boolean;
  isDisabled: boolean;
  type: ContextualButtonItemType.AI_BUTTON;
  buttonText: string;
  buttonIcon?: string;
  buttonSize: Size;
  text?: string;
  isPulsating?: boolean;
  loadingStateText?: string;
}

export interface BaseButtonItem {
  type: ContextualButtonItemType;
  id: string;
  className?: string;
  isDisabled?: boolean;

  onClick(id: string): void;
}

export function ButtonItems({isInlineOption = false, containerClassName = '', ...props}: ButtonItemsProps): ReactElement | null {
  const getButtonItems = (): Array<ReactElement | null> => {
    return props.data.map((item) => {
      switch (item.type) {
        case ContextualButtonItemType.SECONDARY_BUTTON:
          return (
            <ButtonItemSecondary
              key={item.id}
              id={item.id}
              className={item.className}
              buttonIcon={item.buttonIcon}
              onClick={(id): void => {
                return item.onClick(id);
              }}
              text={item.text}
              buttonText={item.buttonText}
            />
          );
        case ContextualButtonItemType.SVG_BUTTON:
          return (
            <ButtonSvgItem
              key={item.id}
              id={item.id}
              className={item.className}
              isDisabled={item.isDisabled}
              text={item.text}
              svgPath={item.svgPath}
              isSelected={item.isSelected}
              onClick={item.onClick}
            />
          );
        case ContextualButtonItemType.CUSTOM_BUTTON:
          return (
            <Button
              id={item.id}
              key={item.id}
              customClasses={item.className}
              size={item.buttonSize}
              text={item.text}
              icon={item.icon}
              type={item.buttonType}
              disabled={item.isDisabled}
              onClick={(): void => {
                item.onClick(item.id);
              }}
              iconPlacement={item.iconPlacement}
            />
          );
        case ContextualButtonItemType.AI_BUTTON:
          return (
            <AiButton
              key={item.id}
              disabled={item.isDisabled}
              isLoading={item.isLoading}
              variant={item.variant}
              text={item.buttonText}
              loadingStateText={item.loadingStateText}
              icon={item.buttonIcon}
              size={item.buttonSize}
              isPulsating={item.isPulsating}
              onClick={(): void => {
                item.onClick(item.id);
              }}
              customClasses={item.className}
            />
          );
        default:
          return null;
      }
    });
  };

  return <div className={`${isInlineOption ? 'flex flex-items-center' : 'spacing-m-t-4 _full-width'}  ${styles.container} ${containerClassName}`}>{getButtonItems()}</div>;
}
